import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'
import type { APIError } from '@/types'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://be.procoach.space/api/v1'
const apiClient = axios.create({ baseURL: API_BASE_URL, timeout: 120000 })

type Session = { access: string | null; refresh: string | null; generation: number }
type AuthRequest = InternalAxiosRequestConfig & { _retry?: boolean; _authGeneration?: number; _authAccess?: string | null }
let session: Session = { access: null, refresh: null, generation: 0 }
let refreshFlight: { generation: number; promise: Promise<Session> } | null = null

// A refresh rotation keeps the generation; changes made by login/logout or another
// browser tab start a new one. Old requests must never retry as that new user.
function currentSession(): Session {
  const access = localStorage.getItem('access_token')
  const refresh = localStorage.getItem('refresh_token')
  if (access !== session.access || refresh !== session.refresh) session = { access, refresh, generation: session.generation + 1 }
  return session
}

function normalizeError(error: AxiosError<APIError>): APIError {
  if (error.response) {
    const data = error.response.data
    return {
      error: data?.error || data?.message || 'Unknown error',
      details: data?.details,
      code: data?.code,
      message: data?.message,
      context: data?.context,
      user_message: data?.user_message,
      ai_usage: data?.ai_usage,
      status: error.response.status,
    }
  }
  return error.request
    ? { error: 'Network error', details: 'Could not connect to server' }
    : { error: 'Request error', details: error.message }
}

async function rotateSession(start: Session): Promise<Session> {
  try {
    const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refresh_token: start.refresh }, { timeout: 120000 })
    if (currentSession().generation !== start.generation) throw new Error('Session changed while refreshing')
    if (typeof data.access_token !== 'string' || !data.access_token || typeof data.refresh_token !== 'string' || !data.refresh_token) throw new Error('Invalid refresh response')
    localStorage.setItem('access_token', data.access_token)
    localStorage.setItem('refresh_token', data.refresh_token)
    session = { access: data.access_token, refresh: data.refresh_token, generation: start.generation }
    return session
  } catch (error) {
    // A late failure belongs to the session that requested the refresh only.
    if (currentSession().generation === start.generation) {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      session = { access: null, refresh: null, generation: start.generation + 1 }
      window.location.href = '/login'
    }
    throw error
  }
}

function refreshSession(start: Session): Promise<Session> {
  if (refreshFlight?.generation === start.generation) return refreshFlight.promise
  const flight = { generation: start.generation, promise: Promise.resolve(start) }
  flight.promise = rotateSession(start).finally(() => { if (refreshFlight === flight) refreshFlight = null })
  refreshFlight = flight
  return flight.promise
}

apiClient.interceptors.request.use(config => {
  const request = config as AuthRequest
  const auth = currentSession()
  if (request._retry && request._authGeneration !== auth.generation) throw new Error('Session changed before retry')
  request._authGeneration = auth.generation
  request._authAccess = auth.access
  if (auth.access) request.headers.set('Authorization', `Bearer ${auth.access}`)
  else request.headers.delete('Authorization')
  return request
})

apiClient.interceptors.response.use(response => response, async (error: AxiosError<APIError>) => {
  const request = error.config as AuthRequest | undefined
  if (error.response?.status === 401 && request && !request._retry) {
    request._retry = true
    const auth = currentSession()
    if (request._authGeneration !== auth.generation) return Promise.reject(normalizeError(error))
    // Another request may already have refreshed while this old 401 was in flight.
    if (request._authAccess !== auth.access && auth.access) return apiClient(request)
    if (auth.refresh) {
      try { await refreshSession(auth) }
      catch { return Promise.reject(normalizeError(error)) }
      if (currentSession().generation !== request._authGeneration) return Promise.reject(normalizeError(error))
      return apiClient(request)
    }
    window.location.href = '/login'
  }
  return Promise.reject(normalizeError(error))
})

export { API_BASE_URL }
export default apiClient
